import React, { useState, useCallback, useEffect } from "react";
import {
  Modal,
  Upload,
  message,
  Row,
  Col,
  Image,
  Spin,
  Typography,
  Card,
  Button,
  Select,
} from "antd";
import { InboxOutlined, FilePdfOutlined, EyeOutlined } from "@ant-design/icons";
import Compressor from "compressorjs";
import axios from "axios";
import { HEADERS_UPIMAGE, POST_UPLOAD_IMAGE_NEW } from "../../../API/apiUrls";

const { Dragger } = Upload;
const { Text } = Typography;

// ── Constants ──────────────────────────────────────────────────
const MENU_TYPES = [
  { id: 1, label: "บอกกล่าว" },
  { id: 2, label: "สิทธิซื้อรถคืน" },
  { id: 3, label: "แจ้งราคาขายรถ" },
  { id: 4, label: "บอกเลิก" },
  { id: 5, label: "คำฟ้อง" },
  { id: 6, label: "ชั้นศาล" },
  { id: 7, label: "เคลียร์เงินเบิกทดลอง" },
  { id: 8, label: "ปิดบัญชี" },
  { id: 9, label: "ชั้นบังคับคดี" },
];

const IMAGE_TYPES = [
  { id: 1, label: "หน้าเว็บไปรษณีย์ไทย" },
  { id: 2, label: "ใบนำส่งไปรษณีย์ไทย" },
  { id: 3, label: "ไฟล์คำฟ้อง" },
  { id: 4, label: "ใบเสร็จศาล(คำฟ้อง)" },
  { id: 5, label: "สลิปเคลียร์เบิก(คำฟ้อง)" },
  { id: 7, label: "พิพากษา" },
  { id: 8, label: "พิพากษา(ทำยอม)" },
  { id: 9, label: "ถอนฟ้อง" },
  { id: 10, label: "ใบเสร็จศาล(พิพากษา)" },
  { id: 11, label: "สลิปเคลียร์เบิก(พิพากษา)" },
  { id: 12, label: "วางคำบังคับ" },
  { id: 13, label: "ใบตัดจ่าย" },
  { id: 14, label: "ใบเสร็จ" },
  { id: 16, label: "หมายตั้ง" },
];

// ── Mapping menuType → imageType ids ──────────────────────────
const MENU_IMAGE_MAP = {
  1: [1, 2],
  2: [1, 2],
  3: [1, 2],
  4: [1, 2],
  5: [3, 4],
  6: [6, 7, 8, 9, 10, 12],
  7: [5, 11],
  8: [13, 14],
  9: [16],
};

// ── PDF Preview Modal ─────────────────────────────────────────
const PdfPreviewModal = ({ open, url, onClose }) => (
  <Modal
    open={open}
    onCancel={onClose}
    footer={null}
    width="90vw"
    style={{ top: 10 }}
    title="ข้อมูล PDF"
  >
    <iframe
      src={url}
      style={{ width: "100%", height: "80vh", border: "none" }}
      title="pdf-preview"
    />
  </Modal>
);

// ── Main Component ────────────────────────────────────────────
const AddImage = ({ open, close, dataDefault, onSuccess }) => {
  const [fileList, setFileList] = useState([]);
  const [allImg, setAllImg] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState("");
  const [selectedMenuType, setSelectedMenuType] = useState(undefined);
  const [selectedImageType, setSelectedImageType] = useState(undefined);

  console.log("dataDefault", dataDefault);

  // ── reset imageType เมื่อเปลี่ยน menuType ─────────────────
  useEffect(() => {
    setSelectedImageType(undefined);
  }, [selectedMenuType]);

  // ── filter IMAGE_TYPES ตาม menuType ───────────────────────
  const filteredImageTypes = selectedMenuType
    ? IMAGE_TYPES.filter((t) =>
        (MENU_IMAGE_MAP[selectedMenuType] ?? []).includes(t.id),
      )
    : [];

  // ── compressImage ──────────────────────────────────────────
  const compressImage = async (file, types, index) => {
    return new Promise((resolve) => {
      let quality;
      if (file.size >= 2500000) quality = 0.1;
      else if (file.size >= 2000000) quality = 0.15;
      else if (file.size >= 1500000) quality = 0.2;
      else if (file.size >= 1000000) quality = 0.3;
      else quality = 0.5;

      new Compressor(file, {
        quality,
        success: (result) => {
          const compressed = new File([result], file.name, {
            type: result.type,
            lastModified: Date.now(),
          });
          setAllImg((prev) => [
            ...prev,
            {
              imageCompressedFile: compressed,
              type: types,
              originalIndex: index,
            },
          ]);
          setUploading(false);
          resolve(true);
        },
        error: (err) => {
          console.error("Compressor error:", err);
          message.error(`ไม่สามารถบีบอัดไฟล์ "${file.name}" ได้`);
          setUploading(false);
          resolve(true);
        },
      });
    });
  };

  // ── getBase64 ──────────────────────────────────────────────
  const getBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  // ── handlePreview ──────────────────────────────────────────
  const handlePreview = async (file) => {
    const isPdf =
      file.type === "application/pdf" ||
      file.name.toLowerCase().endsWith(".pdf");

    if (isPdf) {
      const url =
        file.url ||
        (file.originFileObj ? URL.createObjectURL(file.originFileObj) : null);
      if (url) {
        setPdfPreviewUrl(url);
        setPdfPreviewOpen(true);
      }
      return;
    }

    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }
    setPreviewImage(file.url || file.preview);
    setPreviewOpen(true);
  };

  // ── handleChangeIMG ────────────────────────────────────────
  const handleChangeIMG = ({ fileList: newFileList, file }) => {
    setFileList(newFileList);
    if (file.status === "removed") {
      setAllImg((prev) => prev.filter((img) => img.originalIndex !== file.uid));
    }
    if (["done", "removed", "error"].includes(file.status)) {
      setUploading(false);
    }
  };

  // ── beforeUpts ─────────────────────────────────────────────
  const beforeUpts = async (file, ty) => {
    const isPdf =
      file.type === "application/pdf" ||
      file.name.toLowerCase().endsWith(".pdf");

    if (isPdf) {
      setAllImg((prev) => [
        ...prev,
        { imageCompressedFile: file, type: ty, originalIndex: file.uid },
      ]);
      return false;
    }

    if (file.size > 5000000) {
      message.warning(`ไฟล์ "${file.name}" เกิน 5MB`);
      return Upload.LIST_IGNORE;
    }

    try {
      setUploading(true);
      await compressImage(file, ty, file.uid);
      return false;
    } catch {
      message.error("File processing failed.");
      return Upload.LIST_IGNORE;
    }
  };

  // ── uploadProps ────────────────────────────────────────────
  const uploadProps = {
    name: "file",
    multiple: true,
    accept: "image/*,.pdf",
    listType: "picture",
    fileList,
    onPreview: handlePreview,
    onChange: handleChangeIMG,
    beforeUpload: (file) => beforeUpts(file, selectedImageType),
    iconRender: (file) =>
      file.type === "application/pdf" || file.name.endsWith(".pdf") ? (
        <FilePdfOutlined style={{ fontSize: 48, color: "#ff4d4f" }} />
      ) : null,
  };

  // ── handleUpLoadIMG ────────────────────────────────────────
  const handleUpLoadIMG = useCallback(async () => {
    if (allImg.length === 0) {
      message.warning("กรุณาเลือกไฟล์ที่ต้องการอัปโหลด");
      return;
    }
    if (!selectedMenuType) {
      message.warning("กรุณาเลือกเมนู / ประเภทเอกสาร");
      return;
    }
    if (!selectedImageType) {
      message.warning("กรุณาเลือกประเภทรูปภาพ");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();

      if (dataDefault?.judge?.[0]?.loanId) {
        formData.append("id", dataDefault.judge[0].loanId);
      }

      if (dataDefault?.judge?.[0]?.lawsuitId) {
        formData.append("lawsuitId", dataDefault.judge[0].lawsuitId);
      }

      formData.append("notis", selectedMenuType);

      const sortedImgs = [...allImg]
        .filter((img) => img.originalIndex !== undefined)
        .sort((a, b) =>
          a.type !== b.type
            ? a.type - b.type
            : a.originalIndex - b.originalIndex,
        );

      for (const file of sortedImgs) {
        formData.append("file", file.imageCompressedFile);
        formData.append("menuType", selectedMenuType);
        formData.append("imageType", file.type);
      }

      const res = await axios.post(POST_UPLOAD_IMAGE_NEW, formData, {
        headers: HEADERS_UPIMAGE,
      });

      if (res.status === 200) {
        message.success(`อัปโหลดสำเร็จ ${allImg.length} ไฟล์`);
        setFileList([]);
        setAllImg([]);

        onSuccess?.(); // ✅ trigger reload รูปใน main
      }
    } catch (error) {
      const status = error?.response?.status;
      const serverMsg =
        error?.response?.data?.message ??
        error?.response?.data?.error ??
        error.message;

      const errorMap = {
        400: `ข้อมูลไม่ถูกต้อง: ${serverMsg}`,
        401: "session หมดอายุ กรุณาเข้าสู่ระบบใหม่",
        404: "ไม่พบปลายทาง (404)",
        500: "เกิดข้อผิดพลาดจากเซิร์ฟเวอร์ (500)",
      };
      message.error(
        errorMap[status] ??
          `ไม่สามารถอัปโหลดได้${status ? ` (${status})` : ""}: ${serverMsg}`,
      );
      console.error("❌ handleUpLoadIMG error:", error);
    } finally {
      setUploading(false);
    }
  }, [allImg, selectedMenuType, selectedImageType, dataDefault, onSuccess]);

  // ── handleCancel ───────────────────────────────────────────
  const handleCancel = () => {
    setFileList([]);
    setAllImg([]);
    setPreviewOpen(false);
    setPreviewImage("");
    setPdfPreviewOpen(false);
    setPdfPreviewUrl("");
    setSelectedMenuType(undefined);
    setSelectedImageType(undefined);
    close?.();
  };

  // ── canUpload ──────────────────────────────────────────────
  const canUpload =
    fileList.length > 0 && !!selectedMenuType && !!selectedImageType;

  // ── render ─────────────────────────────────────────────────
  return (
    <>
      <Modal
        title={`อัปโหลดเอกสาร — ${dataDefault?.judge?.[0]?.CONTNO ?? ""}`}
        open={open}
        onOk={handleUpLoadIMG}
        onCancel={handleCancel}
        okText="อัปโหลด"
        cancelText="ยกเลิก"
        width={700}
        confirmLoading={uploading}
        okButtonProps={{ disabled: !canUpload }}
      >
        <Spin spinning={uploading} size="large" tip="กำลังอัปโหลด...">
          {/* ── ข้อมูลสัญญา ── */}
          {dataDefault?.judge?.[0] && (
            <div
              style={{
                background: "#f8f9fc",
                borderRadius: 8,
                padding: "10px 14px",
                marginBottom: 16,
                fontSize: 13,
                color: "#555",
              }}
            >
              <span>สัญญา: </span>
              <strong style={{ color: "#1a1a2e" }}>
                {dataDefault.judge[0].CONTNO ?? "-"}
              </strong>
              {dataDefault.judge[0].SNAM && (
                <>
                  <span style={{ margin: "0 8px" }}>|</span>
                  <span>ลูกหนี้: </span>
                  <strong style={{ color: "#1a1a2e" }}>
                    {dataDefault.judge[0].SNAM}
                    {dataDefault.judge[0].NAME1} {dataDefault.judge[0].NAME2}
                  </strong>
                </>
              )}
            </div>
          )}

          {/* ── เลือก menuType + imageType ── */}
          <Card
            size="small"
            style={{ marginBottom: 16, background: "#f8f9fc" }}
            styles={{ body: { padding: "12px 16px" } }}
          >
            <Row gutter={16}>
              {/* menuType */}
              <Col xs={24} sm={12}>
                <div style={{ marginBottom: 4 }}>
                  <Text strong style={{ fontSize: 13 }}>
                    เมนู / ประเภทเอกสาร
                  </Text>
                  <Text type="danger"> *</Text>
                </div>
                <Select
                  style={{ width: "100%" }}
                  value={selectedMenuType}
                  onChange={(val) => setSelectedMenuType(val)}
                  placeholder="เลือกเมนู"
                  disabled={fileList.length > 0}
                >
                  {MENU_TYPES.map((m) => (
                    <Select.Option key={m.id} value={m.id}>
                      {m.id}. {m.label}
                    </Select.Option>
                  ))}
                </Select>
              </Col>

              {/* imageType — filter ตาม menuType */}
              <Col xs={24} sm={12}>
                <div style={{ marginBottom: 4 }}>
                  <Text strong style={{ fontSize: 13 }}>
                    ประเภทรูปภาพ
                  </Text>
                  <Text type="danger"> *</Text>
                </div>
                <Select
                  style={{ width: "100%" }}
                  value={selectedImageType}
                  onChange={(val) => setSelectedImageType(val)}
                  placeholder={
                    !selectedMenuType ? "เลือกเมนูก่อน" : "เลือกประเภทรูปภาพ"
                  }
                  disabled={!selectedMenuType || fileList.length > 0} // ✅ disable ถ้ายังไม่เลือก menu
                >
                  {filteredImageTypes.map((t) => (
                    <Select.Option key={t.id} value={t.id}>
                      {t.id}. {t.label}
                    </Select.Option>
                  ))}
                </Select>
              </Col>
            </Row>

            {/* hint */}
            <div style={{ marginTop: 8, fontSize: 11, color: "#aaa" }}>
              รองรับไฟล์: JPG, PNG, PDF — ขนาดไม่เกิน 5MB ต่อไฟล์
            </div>
          </Card>

          {/* ── Dragger ── */}
          <Dragger
            {...uploadProps}
            disabled={!selectedMenuType || !selectedImageType}
            style={{ width: "100%", minHeight: 160 }}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined
                style={{
                  color:
                    !selectedMenuType || !selectedImageType
                      ? "#ccc"
                      : "#1677ff",
                  fontSize: 48,
                }}
              />
            </p>
            <p className="ant-upload-text" style={{ fontSize: 15 }}>
              {!selectedMenuType || !selectedImageType
                ? "กรุณาเลือกเมนูและประเภทรูปภาพก่อน"
                : "คลิกหรือลากไฟล์มาวางที่นี่"}
            </p>
            <p className="ant-upload-hint" style={{ fontSize: 12 }}>
              รองรับการอัปโหลดหลายไฟล์พร้อมกัน
            </p>
          </Dragger>

          {/* ── จำนวนไฟล์ ── */}
          {fileList.length > 0 && (
            <div
              style={{
                marginTop: 12,
                fontWeight: 600,
                color: "#5b6ef5",
                fontSize: 13,
              }}
            >
              ไฟล์ที่เลือก: {fileList.length} ไฟล์
            </div>
          )}

          {/* ── Preview grid ── */}
          {fileList.length > 0 && (
            <Row gutter={[8, 8]} style={{ marginTop: 12 }}>
              {fileList.map((file) => {
                const isPdf =
                  file.type === "application/pdf" ||
                  file.name.toLowerCase().endsWith(".pdf");
                const previewUrl = file.originFileObj
                  ? URL.createObjectURL(file.originFileObj)
                  : file.url;

                return (
                  <Col key={file.uid} xs={8} sm={6}>
                    <div
                      style={{
                        border: "1px solid #e8ecf5",
                        borderRadius: 8,
                        padding: 8,
                        textAlign: "center",
                      }}
                    >
                      {isPdf ? (
                        <>
                          <FilePdfOutlined
                            style={{ fontSize: 40, color: "#ff4d4f" }}
                          />
                          <Button
                            size="small"
                            icon={<EyeOutlined />}
                            style={{
                              marginTop: 4,
                              width: "100%",
                              color: "#1677ff",
                            }}
                            onClick={() => {
                              setPdfPreviewUrl(previewUrl);
                              setPdfPreviewOpen(true);
                            }}
                          >
                            ดู PDF
                          </Button>
                        </>
                      ) : (
                        file.originFileObj && (
                          <img
                            src={previewUrl}
                            alt={file.name}
                            style={{
                              width: "100%",
                              height: 80,
                              objectFit: "cover",
                              borderRadius: 4,
                            }}
                          />
                        )
                      )}
                      <p
                        style={{
                          fontSize: 10,
                          marginTop: 4,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          color: "#888",
                        }}
                        title={file.name}
                      >
                        {file.name}
                      </p>
                    </div>
                  </Col>
                );
              })}
            </Row>
          )}
        </Spin>
      </Modal>

      {/* Image Preview */}
      <Image
        style={{ display: "none" }}
        preview={{
          visible: previewOpen,
          onVisibleChange: (v) => setPreviewOpen(v),
          src: previewImage,
        }}
      />

      {/* PDF Preview Modal */}
      <PdfPreviewModal
        open={pdfPreviewOpen}
        url={pdfPreviewUrl}
        onClose={() => {
          setPdfPreviewOpen(false);
          setPdfPreviewUrl("");
        }}
      />
    </>
  );
};

export default AddImage;
